import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Zap, MailPlus, UserRound, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api, API } from '../lib/api';
import { toast } from 'sonner';

const getSessionId = () => {
  let sid = localStorage.getItem('w8_chat_session');
  if (!sid) {
    sid = `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('w8_chat_session', sid);
  }
  return sid;
};

const MODES = [
  { id: 'ai', label: 'Instant Answers', icon: Zap },
  { id: 'message', label: 'Message', icon: MailPlus },
  { id: 'human', label: 'Human Help', icon: UserRound },
];

const WELCOME = "Hi! I'm Steve's Ward 8 Campaign Helper. Ask me about Steve Lesperance, his platform, Council meetings, city data, or how to report a local issue. If you'd rather leave a message or request follow-up from a real person, use the tabs above.";

// ---- Instant answers chat mode (streaming) ----

// Replace / append to the trailing assistant message bubble
const setLastAssistant = (setMessages, content) => setMessages((prev) => {
  const copy = [...prev];
  copy[copy.length - 1] = { role: 'assistant', content };
  return copy;
});
const appendToLastAssistant = (setMessages, delta) => setMessages((prev) => {
  const copy = [...prev];
  copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + delta };
  return copy;
});

// Parse one SSE frame ("data: {json}") and dispatch it
const handleSseFrame = (part, onDelta, onReplace) => {
  const line = part.trim();
  if (!line.startsWith('data: ')) return;
  try {
    const evt = JSON.parse(line.slice(6));
    if (evt.delta) onDelta(evt.delta);
    else if (evt.error) onReplace(evt.error);
  } catch {
    /* skip malformed */
  }
};

// Stream the chat response, invoking callbacks per event
const streamChat = async (text, onDelta, onReplace) => {
  const res = await fetch(`${API}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: getSessionId(), message: text }),
  });
  if (!res.ok || !res.body) throw new Error('Request failed');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop();
    for (const part of parts) handleSseFrame(part, onDelta, onReplace);
  }
};

const CHAT_FALLBACK = 'Sorry, the assistant is temporarily unavailable. Please try again in a moment, or use the Message tab to reach the campaign directly.';

const SuggestedQuestions = ({ settings, onAsk }) => (
  <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-suggested-questions">
    {settings.suggested_questions.map((q, i) => (
      <button key={q} onClick={() => onAsk(q)} className="text-xs font-semibold text-[#1a2744] bg-white border border-[#C9A84C] rounded-full px-3 py-2 hover:bg-[#FBF7EC] text-left" data-testid={`chat-suggested-${i}`}>
        {q}
      </button>
    ))}
  </div>
);

const AiMode = ({ messages, setMessages, settings }) => {
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText || input).trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    try {
      await streamChat(text, (d) => appendToLastAssistant(setMessages, d), (c) => setLastAssistant(setMessages, c));
    } catch {
      setLastAssistant(setMessages, CHAT_FALLBACK);
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, setMessages]);

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#1a2744] text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
              {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : '')}
            </div>
          </div>
        ))}
        {/* Suggested questions — tap to ask */}
        {messages.length <= 1 && !streaming && (settings?.suggested_questions || []).length > 0 && (
          <SuggestedQuestions settings={settings} onAsk={send} />
        )}
      </div>
      <div className="p-3 border-t bg-white shrink-0">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Type your question…"
            data-testid="ai-chat-input"
            className="flex-1 h-11 px-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a2744]"
          />
          <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ai-chat-send" className="h-11 w-11 p-0 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] btn-press">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 leading-tight">{settings?.disclaimer || 'Automated campaign helper. Not an official City of Kawartha Lakes service. Answers may be imperfect; for official services see Who Do I Call?'}</p>
      </div>
    </>
  );
};

// ---- Leave a message mode ----
const MessageMode = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error('Please fill in your name, email, and message.'); return; }
    setBusy(true);
    try {
      await api.post('/contact', { name: form.name.trim(), email: form.email.trim(), subject: 'Campaign Helper: message left for the campaign', message: form.message.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not send your message. Please check your email address.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex-1 overflow-y-auto p-5 bg-slate-50" data-testid="chat-message-success">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0" />
          <div>
            <p className="font-bold text-emerald-900 text-sm">Message received. Thank you!</p>
            <p className="text-xs text-emerald-800 mt-1 leading-relaxed">Steve or a member of the campaign team will read your message. This is a campaign channel, not an official City of Kawartha Lakes service request. For urgent city services, call 705-324-9411.</p>
          </div>
        </div>
        <Button onClick={() => { setDone(false); setForm({ name: '', email: '', message: '' }); }} variant="outline" className="mt-4 h-11 w-full border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="chat-message-another">Leave another message</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-3" data-testid="chat-message-form">
      <p className="text-xs text-slate-600 leading-relaxed">Leave a message for Steve's campaign team. You'll get a reply by email. A real person reads every message.</p>
      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="h-11 bg-white" data-testid="chat-message-name" />
      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" className="h-11 bg-white" data-testid="chat-message-email" />
      <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your message…" rows={5} className="bg-white" data-testid="chat-message-text" />
      <Button type="submit" disabled={busy} className="h-12 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="chat-message-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Message'}
      </Button>
      <p className="text-[10px] text-slate-400 leading-tight">Campaign channel only, not an official City service request.</p>
    </form>
  );
};

// ---- Human escalation mode ----
const HumanMode = ({ settings }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', urgency: 'normal', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const urgencyOptions = settings?.urgency_options?.length ? settings.urgency_options : [
    { value: 'low', label: 'Not urgent (whenever convenient)' },
    { value: 'normal', label: 'Normal (within a few days)' },
    { value: 'high', label: 'Important (as soon as possible)' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { toast.error('Please fill in your name, email, and what you need help with.'); return; }
    setBusy(true);
    try {
      await api.post('/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: `Campaign Helper: HUMAN FOLLOW-UP REQUESTED (urgency: ${form.urgency})`,
        message: form.message.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not send your request. Please check your email address.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex-1 overflow-y-auto p-5 bg-slate-50" data-testid="chat-human-success">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0" />
          <div>
            <p className="font-bold text-emerald-900 text-sm">Follow-up request received</p>
            <p className="text-xs text-emerald-800 mt-1 leading-relaxed">{settings?.escalation_message || 'A real person from the campaign will follow up with you, usually within 1 to 2 business days. For emergencies call 911; for urgent city services call 705-324-9411.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-3" data-testid="chat-human-form">
      <p className="text-xs text-slate-600 leading-relaxed">Prefer to talk to a real person? Leave your details and a campaign team member will follow up.</p>
      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="h-11 bg-white" data-testid="chat-human-name" />
      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" className="h-11 bg-white" data-testid="chat-human-email" />
      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="h-11 bg-white" data-testid="chat-human-phone" />
      <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
        <SelectTrigger className="h-11 bg-white" data-testid="chat-human-urgency"><SelectValue placeholder="How urgent is this?" /></SelectTrigger>
        <SelectContent>
          {urgencyOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What do you need help with?" rows={4} className="bg-white" data-testid="chat-human-text" />
      <Button type="submit" disabled={busy} className="h-12 w-full bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="chat-human-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Request Human Follow-up'}
      </Button>
      <p className="text-[10px] text-slate-400 leading-tight">For emergencies call 911. This is a campaign channel, not an official City service.</p>
    </form>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('ai');
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      fetch(`${API}/chat-settings`)
        .then((r) => r.json())
        .then((s) => {
          setSettings(s);
          fetch(`${API}/chat/history/${getSessionId()}`)
            .then((r) => r.json())
            .then((d) => {
              if (d.messages?.length) setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
              else setMessages([{ role: 'assistant', content: s?.welcome_message || WELCOME }]);
            })
            .catch(() => setMessages([{ role: 'assistant', content: s?.welcome_message || WELCOME }]));
        })
        .catch(() => setMessages([{ role: 'assistant', content: WELCOME }]));
    }
  }, [open]);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-open"
          aria-label="Open Ward 8 Campaign Helper"
          className="fixed bottom-20 md:bottom-6 right-4 z-50 h-14 w-14 rounded-full bg-[#1a2744] text-white shadow-xl flex items-center justify-center border-2 border-[#C9A84C] btn-press hover:bg-[#243456]"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-0 right-0 md:bottom-6 md:right-6 z-50 w-full md:w-[400px] h-[85vh] md:h-[600px] bg-white md:rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="ai-chat-panel">
          <div className="bg-[#1a2744] text-white px-4 pt-3 pb-2 shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-sm">Ask Steve's Ward 8 Campaign Helper</p>
                <p className="text-xs text-slate-300">Instant answers, messages, or human follow-up</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-chat-close" aria-label="Close chat" className="p-2 hover:bg-white/10 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Mode selector */}
            <div className="grid grid-cols-3 gap-1 mt-2.5 bg-white/10 rounded-xl p-1" data-testid="chat-mode-tabs">
              {MODES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setMode(id)}
                  data-testid={`chat-mode-${id}`}
                  className={`flex items-center justify-center gap-1.5 h-10 rounded-lg text-xs font-bold transition-colors duration-150 ${mode === id ? 'bg-[#C9A84C] text-[#1a2744]' : 'text-slate-200 hover:bg-white/10'}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>

          {mode === 'ai' && <AiMode messages={messages} setMessages={setMessages} settings={settings} />}
          {mode === 'message' && <MessageMode />}
          {mode === 'human' && <HumanMode settings={settings} />}
        </div>
      )}
    </>
  );
};
